import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import {
  CARD_MODAL_RADIUS,
  CARD_MODAL_TOP_INSET,
  IOS_BACKDROP_OPACITY
} from "./modalConstants";

const MotionOverlay = motion(ModalOverlay);
const MotionModal = motion(Modal);

export const StyledOverlay = styled(MotionOverlay)<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  display: flex;
  z-index: var(--z-index, ${({ $zIndex }) => $zIndex});
  background-color: rgba(
    0,
    0,
    0,
    var(--backdrop-opacity, ${IOS_BACKDROP_OPACITY})
  );

  &[data-variant="center"] {
    align-items: flex-end;
    justify-content: center;

    @media (min-width: 768px) and (min-height: 768px) {
      align-items: center;
    }
  }

  &[data-variant="sheet"] {
    align-items: flex-end;
    justify-content: center;
  }

  /* The compact alert card is always centered, on every breakpoint. */
  &[data-variant="alert"] {
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 1.6rem;
  }

  /*
   * Opt-in fullscreen desktop treatment for the sheet variant.
   * On mobile it still behaves like a bottom sheet; on desktop the sheet
   * detaches from the bottom, centers, fills most of the viewport, and
   * shows rounded corners on all sides.
   */
  @media (min-width: 768px) {
    &[data-variant="sheet"][data-desktop-fullscreen] {
      align-items: center;
      justify-content: center;
      padding: 2rem;
      box-sizing: border-box;
    }
  }
`;

/**
 * iOS card-style modal — full-screen card on mobile, centered floating on
 * desktop. Size customizations (--width, --height, --max-*, --border-radius)
 * are intentionally desktop-only: mobile must always present the default
 * card layout.
 */
export const StyledCenterModal = styled(MotionModal)`
  background: var(--dialog-background, var(--color-cream, #fafafa));
  border-top-left-radius: ${CARD_MODAL_RADIUS};
  border-top-right-radius: ${CARD_MODAL_RADIUS};
  width: 100%;
  max-width: 700px;
  /* dvh keeps the panel sized correctly under mobile Safari's dynamic chrome. */
  height: calc(100vh - ${CARD_MODAL_TOP_INSET});
  height: calc(100dvh - ${CARD_MODAL_TOP_INSET});
  max-height: 100dvh;
  margin-top: ${CARD_MODAL_TOP_INSET};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.08);
  /* contain establishes a containing block for fixed-position descendants. */
  contain: paint;
  transform-origin: 50% 0;

  @media (min-width: 768px) and (min-height: 768px) {
    border-radius: var(--border-radius, ${CARD_MODAL_RADIUS});
    margin: 0;
    width: var(--width, min(90vw, 540px));
    max-width: var(--max-width, 700px);
    height: var(--height, max(60vh, 520px));
    max-height: var(--max-height, 100dvh);
  }
`;

/** iOS sheet-style modal — bottom-anchored, content-sized, drag-to-dismiss. */
export const StyledSheetModal = styled(MotionModal)`
  position: relative;
  background: var(--dialog-background, var(--color-cream, #fafafa));
  contain: paint;
  border-top-left-radius: var(--border-radius, ${CARD_MODAL_RADIUS});
  border-top-right-radius: var(--border-radius, ${CARD_MODAL_RADIUS});
  width: var(--width, 100%);
  max-width: var(--max-width, 700px);
  max-height: var(--max-height, 85vh);
  max-height: var(--max-height, 85dvh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.08);
  touch-action: none;
  will-change: transform;

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      border-radius: var(--border-radius, ${CARD_MODAL_RADIUS});
      width: 100%;
      height: 100%;
      max-width: none;
      max-height: none;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.16);
    }
  }
`;

/**
 * Compact, content-sized centered card — used by the `alert` variant for
 * confirmation dialogs and prompts. Unlike the iOS card/sheet, it never goes
 * full-height: it hugs its content and stays centered on every breakpoint.
 * Size and surface are themeable via the same custom props as the other
 * variants (`--width`, `--max-width`, `--max-height`, `--border-radius`,
 * `--dialog-background`).
 */
export const StyledAlertModal = styled(MotionModal)`
  background: var(--dialog-background, var(--color-cream, #fffcf3));
  border-radius: var(--border-radius, 0.5rem);
  flex: 0 0 auto;
  width: var(--width, min(680px, 100%));
  max-width: var(--max-width, min(680px, 100%));
  max-height: var(--max-height, 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--light-shadow, 0 8px 40px rgba(0, 0, 0, 0.16));
  transform-origin: 50% 50%;
`;

export const StyledDialog = styled(Dialog)`
  outline: none;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: env(safe-area-inset-bottom, 0);
`;

/** iOS-style grabber pill. */
export const DragHandle = styled("div", {
  shouldForwardProp: (p) => p !== "$overlay"
})<{ $overlay?: boolean }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 0 8px;
  cursor: grab;
  touch-action: none;

  ${({ $overlay }) =>
    $overlay &&
    `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1;
      pointer-events: auto;
    `}

  &:active {
    cursor: grabbing;
  }

  &::before {
    content: "";
    display: block;
    width: 36px;
    height: 5px;
    border-radius: 999px;
    background: ${({ $overlay }) =>
      $overlay ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.18)"};
  }
`;
